import { FC, useMemo } from 'react'
import moment from 'moment'
import {
  ExclamationTriangleIcon,
  ArrowDownTrayIcon,
} from '@heroicons/react/24/outline'
import { Modal } from '../Atoms/Modal'
import { PrimaryButton, SecondaryButton } from '../Atoms/Buttons'
import { Spinner } from '../Atoms/Spinner'
import { ManifestByShipmentDto } from '../../interfaces/Django/ManifestByShipmentDto'
import { exportTableXLSX } from '../../utils/exports'

/** Cuántas guías se listan antes de cortar con "…". El resto va en la descarga. */
const PREVIEW_LIMIT = 3

export interface MissingGuidesModalProps {
  open: boolean
  onClose: () => void
  /** Guías de la carga que NO aparecieron en la lista de despachables. */
  requested: string[]
  /**
   * Guías que SÍ están en la lista pero declaran 0 piezas. Causa distinta y
   * arreglo distinto: no hay que buscarlas, hay que corregirles el dato.
   */
  withoutPieces?: string[]
  /** Respuesta de POST /manifest/by-shipment. Vacío mientras carga o si falló. */
  results: ManifestByShipmentDto[]
  loading: boolean
  /** Mensaje si la consulta falló (o el endpoint todavía no existe). */
  error: string | null
  /**
   * NINGUNA guía de la carga se puede despachar, así que no hay manifiesto que
   * crear. Cambia el copy: no es un aviso al costado del flujo, es el motivo por
   * el que el flujo no se abrió.
   */
  allUnusable?: boolean
}

const fmt = (d?: string | null) =>
  d ? moment(d).format('DD/MM/YYYY, hh:mm a') : ''

/**
 * Explica por qué algunas guías de la carga no están en la lista de despachables.
 *
 * El operador cargó 1500 guías y en el modal ve 1497: sin esto, las 3 que faltan son
 * un conteo mudo. Acá se dicen CUÁLES y, cuando el backend lo sabe, en qué manifiesto
 * quedaron — que es la respuesta que hace falta para decidir si seguir o parar.
 *
 * Se muestran hasta PREVIEW_LIMIT y el resto se corta con "…": la lista puede tener
 * cientos de guías y un modal con scroll infinito no se lee. La descarga lleva TODO,
 * incluido lo que la pantalla no muestra, y por eso está disponible en los dos casos.
 */
export const MissingGuidesModal: FC<MissingGuidesModalProps> = ({
  open,
  onClose,
  requested,
  withoutPieces = [],
  results,
  loading,
  error,
  allUnusable = false,
}) => {
  const { manifested, notFound } = useMemo(() => {
    const byNumber = new Map(results.map((r) => [r.shipment_number, r]))
    const m: ManifestByShipmentDto[] = []
    const nf: string[] = []
    requested.forEach((n) => {
      const hit = byNumber.get(n)
      if (hit && hit.manifests.length > 0) m.push(hit)
      else nf.push(n)
    })
    return { manifested: m, notFound: nf }
  }, [requested, results])

  const download = () => {
    const columns = [
      'Guía',
      'Manifiesto',
      'Estatus del manifiesto',
      'Tipo',
      'Tienda origen',
      'Tienda destino',
      'Fecha de creación',
      'Fecha de cierre',
      'Manifiesto de recepción',
    ]
    const rows: (string | number)[][] = []
    manifested.forEach((r) =>
      // Una fila POR MANIFIESTO, no por guía: una guía puede estar en el de despacho
      // y en el de recepción, y colapsarlos escondería justo el dato que se busca.
      r.manifests.forEach((m) =>
        rows.push([
          r.shipment_number,
          m.code ?? '',
          m.status?.name ?? '',
          m.manifest_type_id ?? '',
          m.origin_store?.code ?? '',
          m.destination_store?.code ?? '',
          fmt(m.creation_date),
          fmt(m.closed_date),
          m.reception_manifest?.code ?? '',
        ])
      )
    )
    notFound.forEach((n) =>
      rows.push([n, 'No se encontró en ningún manifiesto', '', '', '', '', '', '', ''])
    )
    withoutPieces.forEach((n) =>
      rows.push([n, 'Sin piezas declaradas', '', '', '', '', '', '', ''])
    )
    exportTableXLSX(columns, rows, 'Guías', 'guias_no_incluidas')
  }

  const total = requested.length + withoutPieces.length

  return (
    <Modal
      open={open}
      setOpen={(v) => !v && onClose()}
      onClose={() => onClose()}
      onEscape={onClose}
      className="w-full max-w-[95vw]"
      style={{ maxWidth: '38rem' }}
    >
      <div className="space-y-4">
        <div className="flex items-start gap-3">
          <ExclamationTriangleIcon className="mt-0.5 h-6 w-6 shrink-0 text-yellow-700" />
          <div>
            <h3 className="text-base font-semibold text-gray-900">
              {allUnusable
                ? 'No hay guías de esta carga para manifestar'
                : `${total} guía${total === 1 ? '' : 's'} de esta carga no ${
                    total === 1 ? 'se puede' : 'se pueden'
                  } incluir`}
            </h3>
            <p className="mt-0.5 text-sm text-gray-500">
              {allUnusable
                ? 'Por eso no se abrió la creación de manifiesto: un manifiesto tiene que llevar al menos una guía de esta carga.'
                : // El subtítulo NO puede decir "no aparecen en la lista": las que no
                  // declaran piezas SÍ están en la lista. El detalle de cada causa va
                  // en los bloques de abajo.
                  `${total === 1 ? 'Abajo está el motivo' : 'Abajo están los motivos'}.`}
            </p>
          </div>
        </div>

        {loading && (
          <div className="flex items-center gap-2 text-sm text-gray-600">
            <Spinner />
            Consultando en qué manifiesto quedaron…
          </div>
        )}

        {!loading && error && (
          <p className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3 text-sm text-amber-800">
            No se pudo verificar en qué manifiesto quedaron ({error}). Podés
            descargar el listado igual y validarlo con soporte.
          </p>
        )}

        {!loading && !error && manifested.length > 0 && (
          <div>
            <p className="mb-1 text-sm font-semibold text-gray-800">
              Ya {manifested.length === 1 ? 'está' : 'están'} en un manifiesto:
            </p>
            <ul className="space-y-1 text-sm text-gray-700">
              {manifested.slice(0, PREVIEW_LIMIT).map((r) => (
                <li key={r.shipment_number}>
                  <span className="font-semibold">{r.shipment_number}</span> —
                  manifiesto{' '}
                  <span className="font-semibold">{r.manifests[0].code}</span>
                  {r.manifests[0].status?.name
                    ? ` (${r.manifests[0].status.name})`
                    : ''}
                </li>
              ))}
              {manifested.length > PREVIEW_LIMIT && (
                <li className="text-gray-500">
                  … y {manifested.length - PREVIEW_LIMIT} más
                </li>
              )}
            </ul>
          </div>
        )}

        {/* `!error` es obligatorio: si la consulta falló, `results` viene vacío y
            TODAS las guías caerían acá. Diría "no se encuentran" de guías que ni
            siquiera se llegaron a verificar. Con error manda el aviso ámbar. */}
        {!loading && !error && notFound.length > 0 && (
          <div className="rounded-md border border-red-200 bg-red-50 px-4 py-3">
            <p className="text-sm font-semibold text-red-800">
              Guía{notFound.length === 1 ? '' : 's'}{' '}
              {notFound.slice(0, PREVIEW_LIMIT).join(', ')}
              {notFound.length > PREVIEW_LIMIT ? ', …' : ''}: no se{' '}
              {notFound.length === 1 ? 'encuentra' : 'encuentran'} dentro de la
              lista. Contacten a Soporte Técnico para su validación.
            </p>
          </div>
        )}

        {withoutPieces.length > 0 && (
          <div className="rounded-md border border-amber-200 bg-amber-50 px-4 py-3">
            <p className="text-sm font-semibold text-amber-800">
              Guía{withoutPieces.length === 1 ? '' : 's'}{' '}
              {withoutPieces.slice(0, PREVIEW_LIMIT).join(', ')}
              {withoutPieces.length > PREVIEW_LIMIT ? ', …' : ''}: no{' '}
              {withoutPieces.length === 1 ? 'declara' : 'declaran'} piezas, así que
              no {withoutPieces.length === 1 ? 'puede' : 'pueden'} viajar en un
              manifiesto.
            </p>
          </div>
        )}

        <div className="flex flex-wrap items-center justify-end gap-2 pt-2">
          {/* Disponible SIEMPRE, incluso si la consulta falló: el listado de guías
              es lo único accionable que el operador se puede llevar a soporte. */}
          <SecondaryButton
            className="!py-1.5 !text-xs items-center gap-1.5"
            onClick={download}
          >
            <ArrowDownTrayIcon className="h-4 w-4" aria-hidden="true" />
            Descargar listado
          </SecondaryButton>
          <PrimaryButton className="!py-1.5 !text-xs" onClick={onClose}>
            Entendido
          </PrimaryButton>
        </div>
      </div>
    </Modal>
  )
}
